import React, {Dispatch, SetStateAction, useCallback} from 'react'

import {media} from 'styled-bootstrap-grid'
import styled from 'styled-components'

import {
  Combobox,
  MultiSelectDropdown,
  RadixToggleGroup,
} from '@festi/common/components/form'
import {styledTheme} from '@festi/common/themes'

import InfoBox from 'src/components/common/redesign/InfoBox'

import {KycAnswer, usePago} from '../PagoContext'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const Questions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: end;
  gap: 32px;
  padding-bottom: 32px;
  overflow-y: auto;

  ${media.sm`
    grid-template-columns: 1fr 1fr;
  `}
`

const ToggleGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Label = styled.label`
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  color: ${styledTheme.palette.blue};
  pointer-events: none;
`

const ToggleGroupWrapper = styled.div`
  width: 100%;

  ${media.sm`
    width: fit-content;
  `}
`

interface KycQuestion {
  kycAnswers: KycAnswer
  setKycAnswers: Dispatch<SetStateAction<KycAnswer>>
}

export default function PagoKycQuestions({
  kycAnswers,
  setKycAnswers,
}: KycQuestion) {
  const {kycQuestions} = usePago()

  const handleAnswerChange = useCallback(
    (questionId: number, newValue: string | string[]) => {
      setKycAnswers((prev) => ({
        ...prev,
        [questionId]: newValue,
      }))
    },
    [setKycAnswers],
  )

  return (
    <Root>
      <InfoBox
        large
        info="Til að fara að lögum gegn peningaþvætti og fjármögnun hryðjuverka þurfum við nokkur svör."
      />
      <form>
        <Questions>
          {kycQuestions?.map((q) => {
            if (q.type === 'multiple') {
              return (
                <MultiSelectDropdown
                  key={q.id}
                  label={q.question}
                  items={q.answers}
                  placeholder="Veldu svar"
                  values={kycAnswers[q.id] as string[]}
                  setValues={(newVal) => handleAnswerChange(q.id, newVal)}
                />
              )
            } else if (q.type === 'single') {
              if (q.answers.length === 2) {
                return (
                  <ToggleGroupContainer key={q.id}>
                    <Label>{q.question}</Label>
                    <ToggleGroupWrapper>
                      <RadixToggleGroup
                        selected={kycAnswers[q.id] as string}
                        items={q.answers.map((answer) => ({
                          value: answer,
                        }))}
                        handleChange={(newVal) =>
                          handleAnswerChange(q.id, newVal)
                        }
                        textVariant
                      />
                    </ToggleGroupWrapper>
                  </ToggleGroupContainer>
                )
              } else {
                return (
                  <Combobox
                    key={q.id}
                    label={q.question}
                    placeholder={'Veldu svar'}
                    items={q.answers.map((answer) => ({
                      value: answer,
                      label: answer,
                    }))}
                    onSelect={(newVal) => handleAnswerChange(q.id, newVal)}
                  />
                )
              }
            }
          })}
        </Questions>
      </form>
    </Root>
  )
}
